import {useCallback, useEffect, useRef, useState} from 'react';
import {AnyCryptoDepositScreen} from 'src/v2/components/anyCrypto/deposit/AnyCryptoDepositScreen';
import {QuoteErrorScreen} from 'src/v2/components/anyCrypto/QuoteErrorScreen';
import {usePaymentPollingAnyCrypto} from 'src/v2/components/anyCrypto/usePaymentPollingAnyCrypto';
import {useRequestRelayQuote} from 'src/v2/components/anyCrypto/useRequestRelayQuote';
import {useAllocateDepositAddress} from 'src/v2/components/anyCrypto/useAllocateDepositAddress';
import {ScreenContentLayout} from 'src/v2/components/screen/ScreenContentLayout';
import {useSelector, setValue} from 'src/v2/state/store';
import {isRelaySupportedCurrency} from 'src/v2/components/anyCrypto/relayConstants';
import {formatCurrencyAmountToMajorUnitsFixed} from 'src/utils/helpers';
import {isStablecoin} from 'src/v2/util/isStablecoin';
import {useRecurringCallback} from 'src/v2/components/anyCrypto/deposit/useRecurringCallback';
import {useInactivityTimeout} from 'src/v2/components/anyCrypto/deposit/useInactivityTimeout';
import type {
  OpenapiPostV1CryptoInternalRelayQuoteInputInputCurrencyEnum,
  OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum,
} from '@stripe-internal/data-v1/schema/types';

const QUOTE_REFRESH_INTERVAL_S = 60;

export const AnyCryptoScreen = () => {
  const isRelayFlow = useSelector(
    (state) => state.featureFlags.enable_pay_with_any_crypto,
  );
  const {startPolling} = usePaymentPollingAnyCrypto();
  const requestRelayQuoteBase = useRequestRelayQuote();
  const navigateToAnyCryptoProgress = () => {
    setValue((state) => {
      state.screen = 'ANY_CRYPTO_PROGRESS';
    });
  };
  const [initialQuoteFailed, setInitialQuoteFailed] = useState(false);
  const hasLoadedQuoteRef = useRef(false);
  const maxRelayRequestStatus = useSelector(
    (state) => state.anyCryptoDeposit.maxRelayRequestStatus,
  );
  const sessionState = useSelector((state) => state.state);
  const selectedToken = useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );
  const selectedNetwork = useSelector(
    (state) => state.anyCryptoDeposit.selectedNetwork,
  );
  const directDepositSupportedCurrencies = useSelector(
    (state) => state.directDepositSupportedCurrencies,
  );
  const relayQuote = useSelector((state) => state.anyCryptoDeposit.quote);
  const paymentAmount = useSelector((state) => state.paymentDetails.amount);
  const paymentCurrency = useSelector((state) => state.paymentDetails.currency);
  const isStableQuote = isStablecoin(selectedToken);
  const selectedRelayCurrency = directDepositSupportedCurrencies
    .filter(isRelaySupportedCurrency)
    .find(
      (currency) =>
        currency.assetCode === selectedToken &&
        currency.currencyNetwork === selectedNetwork,
    );

  const {isInactive, resume} = useInactivityTimeout();

  const {address: allocatedAddress} = useAllocateDepositAddress(
    isRelayFlow
      ? {
          network: null,
          currency: null,
        }
      : {
          network: selectedNetwork,
          currency: selectedToken,
        },
  );

  useEffect(() => {
    if (maxRelayRequestStatus !== null && maxRelayRequestStatus !== 'waiting') {
      setValue((state) => {
        state.screen = 'ANY_CRYPTO_PROGRESS';
      });
    }
  }, [maxRelayRequestStatus]);

  useEffect(() => {
    if (
      !isRelayFlow &&
      (sessionState === 'await_deposit' || sessionState === 'purchase_complete')
    ) {
      setValue((state) => {
        state.screen = 'ANY_CRYPTO_PROGRESS';
      });
    }
  }, [isRelayFlow, sessionState]);

  const requestRelayQuote = useCallback(
    async (
      inputCurrency: OpenapiPostV1CryptoInternalRelayQuoteInputInputCurrencyEnum,
      inputChain: OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum,
    ) => {
      try {
        const result = await requestRelayQuoteBase(inputCurrency, inputChain);
        hasLoadedQuoteRef.current = true;
        return result;
      } catch (err) {
        if (!hasLoadedQuoteRef.current) {
          setInitialQuoteFailed(true);
        }
        throw err;
      }
    },
    [requestRelayQuoteBase],
  );

  useEffect(() => {
    if (allocatedAddress || relayQuote.depositAddress) {
      startPolling();
    }
  }, [allocatedAddress, relayQuote.depositAddress, startPolling]);

  const handleRefreshRelayQuote = useCallback(async () => {
    if (!selectedRelayCurrency) return;

    return requestRelayQuote(
      selectedRelayCurrency.assetCode,
      selectedRelayCurrency.currencyNetwork,
    );
  }, [requestRelayQuote, selectedRelayCurrency]);

  const shouldRequestRelayQuote =
    isRelayFlow && !!selectedRelayCurrency && !isInactive;

  const {secondsUntilRun: secondsUntilRefresh} = useRecurringCallback(
    shouldRequestRelayQuote
      ? {
          callback: handleRefreshRelayQuote,
          intervalSeconds: QUOTE_REFRESH_INTERVAL_S,
          runImmediately: true,
          maxRuns: isStableQuote ? 1 : undefined,
        }
      : {skip: true},
  );

  if (initialQuoteFailed) {
    return (
      <ScreenContentLayout
        hideTitle
        onBack={() =>
          setValue((state) => {
            state.screen = 'TOKEN_SELECT';
          })
        }
      >
        <QuoteErrorScreen />
      </ScreenContentLayout>
    );
  }

  const depositData = isRelayFlow
    ? {
        depositAddress: relayQuote.depositAddress,
        amount: relayQuote.inputAmount,
      }
    : {
        depositAddress: allocatedAddress || null,
        amount: formatCurrencyAmountToMajorUnitsFixed(
          paymentAmount,
          paymentCurrency,
        ),
      };

  return (
    <ScreenContentLayout
      onBack={() =>
        setValue((state) => {
          state.screen = 'ANY_CRYPTO_REFUND';
        })
      }
    >
      <AnyCryptoDepositScreen
        depositAddress={depositData.depositAddress}
        amount={depositData.amount}
        token={selectedToken}
        network={selectedNetwork}
        onCheckPaymentStatus={
          isRelayFlow ? navigateToAnyCryptoProgress : undefined
        }
        isInactive={isInactive}
        onResume={resume}
        refreshCountdown={
          secondsUntilRefresh !== null
            ? {
                secondsUntilRefresh,
                totalSeconds: QUOTE_REFRESH_INTERVAL_S,
              }
            : null
        }
      />
    </ScreenContentLayout>
  );
};
